import { useEffect, useRef, useState } from "react";
import {
  LiaHornbill,
  LiaSearchPlusSolid,
  LiaSearchSolid,
} from "react-icons/lia";
import { MdClose, MdPrint } from "react-icons/md";
import { orderPlanView, salesOrderMaster } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import Pagination from "../../common/Pagination";
import BatchTable from "./BatchTable";
import Button from "../../common/Button";
import { formatDate } from "../../utlis/formateDate";
import Model from "../../common/Model";
import InputField from "../../common/InputField";
import { LayoutGrid } from "lucide-react";
import { RiHistoryLine } from "react-icons/ri";
import { STATUS_COLORS } from "../../utlis/constant";

const OrderPlanView = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOrderplanData, setIsOrderPlanData] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [batchItems, setBatchItems] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [viewData, setViewData] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("sales");
  const [salesPage, setSalesPage] = useState(1);
  const [salesHasMore, setSalesHasMore] = useState(true);
  const [executedData, setExecutedData] = useState([]);
  const [executedPage, setExecutedPage] = useState(1);
  const [executedHasMore, setExecutedHasMore] = useState(true);
  const pageSize = 50;
  const tableScrollRef = useRef(null);
  const isFetchingRef = useRef(false);
  const [loading, setLoading] = useState(false);

  const fetchOrderplanData = async (page) => {
    try {
      setLoading(true);
      const res = await orderPlanView.getAllPlanOrders(page, pageSize);

      setIsOrderPlanData((prev) => {
        return page === 1 ? res.data || [] : [...prev, ...(res.data || [])];
      });

      if (!res.data || res.data.length < pageSize) {
        setSalesHasMore(false);
      }
      isFetchingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const fetchExecutedOrders = async (page) => {
    try {
      setLoading(true);
      const res = await orderPlanView.getAllExecutedOrders(page, pageSize);

      setExecutedData((prev) => {
        return page === 1 ? res.items || [] : [...prev, ...(res.items || [])];
      });

      if (!res.items || res.items.length < pageSize) {
        setExecutedHasMore(false);
      }
      isFetchingRef.current = false;
    } finally {
      setLoading(false);
    }
  };
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "sales") {
      setSalesPage(1);
      setIsOrderPlanData([]);
      setSalesHasMore(true);
    } else {
      setExecutedPage(1);
      setExecutedData([]);
      setExecutedHasMore(true);
    }
  };

  useEffect(() => {
    if (activeTab === "sales") fetchOrderplanData(salesPage);
  }, [salesPage, activeTab]);

  useEffect(() => {
    if (activeTab === "executed") fetchExecutedOrders(executedPage);
  }, [executedPage, activeTab]);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;

        if (activeTab === "sales" && salesHasMore) {
          setSalesPage((p) => p + 1);
        }

        if (activeTab === "executed" && executedHasMore) {
          setExecutedPage((p) => p + 1);
        }
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [activeTab, loading, salesHasMore, executedHasMore]);

  const handleView = async (id) => {
    setLoading(true);
    try {
      const res = await salesOrderMaster.getById(id);
      setViewData(res.data);
      setIsViewModalOpen(true);
    } catch (err) {
      notify("Failed to load order details", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setIsOrderPlanData(isOrderplanData);
    } else {
      const filtered = isOrderplanData.filter((d) =>
        d.status_Name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setIsOrderPlanData(filtered);
    }
  }, [searchQuery, isOrderplanData]);

  const handlePrint = async (order_No) => {
    try {
      setLoading(true);

      const blob = await orderPlanView.getByPrintId(order_No);
      if (!blob) {
        notify("No PDF received from server", "warning");
        return;
      }

      const file = new Blob([blob], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);

      const printWindow = window.open(fileURL, "_blank");

      printWindow.onload = () => {
        printWindow.print();
      };

      notify("PDF opened successfully", "success");
    } catch (err) {
      console.error("Print Error:", err);
      notify("Failed to open PDF", "error");
    } finally {
      setLoading(false);
    }
  };

  const executedColumns = [
    { key: "order_No", label: "Order No" },
    { key: "batch_Id", label: "Batch Id" },
    { key: "total_Qty", label: "Qty" },
    {
      key: "order_Date",
      label: "Ord Date",
      render: (value) => formatDate(value),
    },
    {
      key: "dispatch_Date",
      label: "Dispatch Date",
      render: (value) => formatDate(value),
    },
    { key: "general_Notes", label: "Remarks" },
  ];

  const columns = [
    {
      key: "select",
      label: (
        <input
          type="checkbox"
          checked={
            isOrderplanData.length > 0 &&
            selectedIds.length ===
              isOrderplanData.filter((i) => i?.id).map((i) => i.id).length
          }
          onChange={(e) => {
            if (e.target.checked) {
              const allIds = isOrderplanData
                .filter((i) => i?.id)
                .map((i) => i.id);
              setSelectedIds(allIds);
            } else {
              setSelectedIds([]);
            }
          }}
        />
      ),
      render: (_value, row) =>
        row?.id ? (
          <input
            type="checkbox"
            checked={selectedIds.includes(row.id)}
            onChange={() => {
              setSelectedIds((prev) =>
                prev.includes(row.id)
                  ? prev.filter((id) => id !== row.id)
                  : [...prev, row.id],
              );
            }}
          />
        ) : null,
    },
    { key: "order_No", label: "SO NO" },
    {
      key: "die_Status",
      label: "Die Status",
      render: (val) => {
        const color = STATUS_COLORS[val] || "bg-slate-100 text-slate-600";
        return (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}
          >
            {val}
          </span>
        );
      },
    },
    { key: "cust_Name", label: "Customer" },
    { key: "dieNumber", label: "Die no" },
    { key: "currentWeight", label: "Current Wt" },
    { key: "profile_Length", label: "Cut Length" },
    { key: "qty", label: "Pcs" },
    { key: "qty_Weight", label: "WT" },
    { key: "alloy_Name", label: "Alloy" },
    { key: "req_Delivery", label: "Req. Delivery" },
    { key: "status", label: "Dispatch Days." },
  ];

  const DetailItem = ({ label, value, highlight, isDate }) => (
    <div className="flex flex-col">
      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
        {label}
      </span>
      <span
        className={`text-sm font-semibold ${highlight ? "text-sky-700" : "text-slate-800"}`}
      >
        {value || "—"}
      </span>
    </div>
  );

  return (
    <>
      <div>
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-1">
            <div className="space-y-1">
              <h2 className="text-2xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
                Order Plan View
              </h2>
              <p className="text-sm text-slate-500 font-medium">
                Manage and track customer sales orders efficiently
              </p>
            </div>

            <div className="inline-flex p-1 bg-slate-200/50 rounded-xl backdrop-blur-sm">
              <button
                onClick={() => handleTabChange("sales")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === "sales"
                    ? "bg-white text-primary shadow-sm"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                <LayoutGrid size={18} /> Sales Orders
              </button>
              <button
                onClick={() => handleTabChange("executed")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === "executed"
                    ? "bg-white text-primary shadow-sm"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                <RiHistoryLine size={18} /> Executed Orders
              </button>
            </div>
          </div>

          {activeTab === "sales" && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="relative w-full sm:w-80 group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <LiaSearchSolid className="h-5 w-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                </div>
                <input
                  type="text"
                  className="w-full bg-slate-50 border border-slate-200 py-2.5 pl-11 pr-10 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all placeholder:text-slate-400 text-sm"
                  placeholder="Search by order, customer..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-red-500 transition-colors"
                  >
                    <MdClose size={20} />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto">
                <Button
                  text={`Add To Batch (${selectedIds.length})`}
                  icon={LiaHornbill}
                  disabled={selectedIds.length === 0}
                  onClick={async () => {
                    if (selectedIds.length === 0) return;
                    try {
                      const res = await salesOrderMaster.AddToBatch({
                        SelectedIds: selectedIds,
                      });
                      setBatchItems(res || []);
                      setBatchId(res.batchId || "");
                      setIsModelOpen(true);
                    } catch (err) {
                      notify("Failed to add to batch", "error");
                    }
                  }}
                  className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 ${
                    selectedIds.length > 0
                      ? "bg-orange text-white shadow-lg shadow-orange-200 hover:bg-orange-700 hover:-translate-y-0.5 active:scale-95 cursor-pointer opacity-100 visibility-visible"
                      : "bg-slate-100 text-slate-400 cursor-not-allowed opacity-60"
                  }`}
                  iconSpacing="mr-2"
                  iconColor={
                    selectedIds.length > 0 ? "text-white" : "text-slate-400"
                  }
                />
              </div>
            </div>
          )}
        </div>
        <div
          ref={tableScrollRef}
          className="relative overflow-auto border border-slate-200 rounded-xl shadow-inner bg-white"
          style={{ height: "calc(100vh - 320px)" }}
        >
          {loading && salesPage && executedPage === 1 ? (
            <Loader />
          ) : activeTab === "sales" ? (
            <div className="min-w-max">
              <Table
                columns={columns}
                data={isOrderplanData}
                showActions={false}
              />
            </div>
          ) : (
            <div className="min-w-max">
              <Table
                columns={executedColumns}
                data={executedData}
                onView={(row) => handleView(row.order_No)}
              />
            </div>
          )}
        </div>
        {activeTab === "sales" && (
          <div className="flex justify-center mb-4">
            <div className="bg-[#1e293b] text-white px-8 py-3 rounded-2xl shadow-2xl flex items-center gap-12 border border-slate-700 animate-bounce-subtle">
              <div className="flex flex-col items-center">
                <span className="text-[10px] uppercase tracking-[2px] text-slate-400 font-bold">
                  Total Pieces
                </span>
                <span className="text-xl font-black">
                  {isOrderplanData.reduce(
                    (sum, item) => sum + (Number(item.qty) || 0),
                    0,
                  )}
                </span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-[10px] uppercase tracking-[2px] text-slate-400 font-bold">
                  Total Weight
                </span>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-black text-orange-400">
                    {isOrderplanData
                      .reduce(
                        (sum, item) => sum + (Number(item.qty_Weight) || 0),
                        0,
                      )
                      .toFixed(3)}
                  </span>
                  <span className="text-[10px] text-slate-400 font-bold">
                    KG
                  </span>
                </div>
              </div>
              <div className="h-8 w-[1px] bg-slate-600"></div>

              <div className="flex flex-col items-center">
                <span className="text-[10px] uppercase tracking-[2px] text-slate-400 font-bold">
                  Selected Pieces
                </span>
                <span className="text-xl font-black">
                  {isOrderplanData
                    .filter((item) => selectedIds.includes(item.id))
                    .reduce((sum, item) => sum + (Number(item.qty) || 0), 0)}
                </span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-[10px] uppercase tracking-[2px] text-slate-400 font-bold">
                  Selected Weight
                </span>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-black text-orange-400">
                    {isOrderplanData
                      .filter((item) => selectedIds.includes(item.id))
                      .reduce(
                        (sum, item) => sum + (Number(item.qty_Weight) || 0),
                        0,
                      )
                      .toFixed(3)}
                  </span>
                  <span className="text-[10px] text-slate-400 font-bold">
                    KG
                  </span>
                </div>
              </div>

              <button
                onClick={() => setSelectedIds([])}
                className="bg-slate-700/50 p-2 rounded-full hover:bg-red-500 transition-colors"
              >
                <MdClose size={18} />
              </button>
            </div>
          </div>
        )}
        {isModelOpen && (
          <BatchTable
            batchItems={batchItems}
            batchId={batchId}
            onClose={() => {
              setIsModelOpen(false);
              setIsOrderPlanData([]);
              if (salesPage === 1) {
                fetchOrderplanData(1);
              } else {
                setSalesPage(1);
              }
              setSalesHasMore(true);
              setSelectedIds([]);
              setBatchItems([]);
              setBatchId("");
            }}
          />
        )}
        <Model
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          name="Executed Order Details"
        >
          {viewData && (
            <div className="flex flex-col gap-6 p-1">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-y-5 gap-x-8 bg-slate-50 p-5 rounded-xl border border-slate-100">
                <DetailItem
                  label="Order Number"
                  value={viewData.order_No}
                  highlight
                />
                <DetailItem
                  label="Customer Name"
                  value={viewData.customerName}
                />
                <DetailItem label="Order Alloy" value={viewData.aolly_Name} />
                <DetailItem
                  label="Order Date"
                  value={formatDate(viewData.order_Date)}
                />
                <DetailItem
                  label="Delivery Days"
                  value={`${viewData.delivery_Days} Days`}
                />
                <DetailItem
                  label="Calculated Dispatch"
                  value={formatDate(viewData.calculate_dispatch_date)}
                  isDate
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-4 px-1">
                  <div className="flex items-center gap-2">
                    <span className="h-6 w-1 bg-sky-600 rounded-full"></span>
                    <h3 className="text-lg font-bold text-slate-800 uppercase tracking-tight">
                      Order Items
                    </h3>
                  </div>
                  <Button
                    icon={MdPrint}
                    text="Print Invoice"
                    className="bg-sky-50 hover:bg-sky-100 text-sky-700 px-4 py-2 rounded-lg transition-all border border-sky-200 flex items-center gap-2 font-medium"
                    onClick={() => handlePrint(viewData.order_No)}
                  />
                </div>

                <div className="overflow-hidden border border-slate-200 rounded-xl shadow-sm">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-800 text-white text-xs uppercase tracking-wider">
                        <th className="p-3 font-semibold">Die Profile</th>
                        <th className="p-3 font-semibold text-center">
                          Weight
                        </th>
                        <th className="p-3 font-semibold">Unit</th>
                        <th className="p-3 font-semibold text-center">
                          Length
                        </th>
                        <th className="p-3 font-semibold text-center">
                          Quantity
                        </th>
                        <th className="p-3 font-semibold text-center">
                          Total Wt.
                        </th>
                        <th className="p-3 font-semibold">Coating Color</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white">
                      {viewData?.orderDetails?.map((d, i) => (
                        <tr
                          key={i}
                          className="hover:bg-blue-50/50 transition-colors text-slate-700"
                        >
                          <td className="p-3 font-medium text-slate-900">
                            {d.dieNumber}
                          </td>
                          <td className="p-3 text-center">{d.currentWeight}</td>
                          <td className="p-3">{d.unit_Conv_Name}</td>
                          <td className="p-3 text-center">
                            {d.profile_Length}
                          </td>
                          <td className="p-3 text-center font-bold text-sky-700">
                            {d.qty} pcs
                          </td>
                          <td className="p-3 text-center font-bold">
                            {d.qty_Weight} kg
                          </td>
                          <td className="p-3">
                            <span className="px-2 py-1 bg-slate-100 rounded text-xs border border-slate-200">
                              {d.powder_Coating_Color || "-"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="bg-amber-50/50 p-4 rounded-lg border border-amber-100">
                    <h4 className="text-xs font-bold text-amber-700 uppercase mb-2">
                      Packing Instructions
                    </h4>
                    <p className="text-sm text-slate-700 italic">
                      "
                      {viewData.packingInstructions ||
                        "No specific instructions provided."}
                      "
                    </p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                    <h4 className="text-xs font-bold text-slate-500 uppercase mb-2">
                      General Notes
                    </h4>
                    <p className="text-sm text-slate-700">
                      {viewData.generalNotes || "NA"}
                    </p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 text-white shadow-lg self-start">
                  <h3 className="text-slate-400 text-xs font-bold uppercase mb-4 tracking-widest">
                    Order Summary
                  </h3>
                  <div className="flex justify-between items-end border-b border-slate-700 pb-4 mb-4">
                    <span className="text-slate-300">Total Quantity</span>
                    <span className="text-2xl font-bold">
                      {viewData?.total_Qty}{" "}
                      <small className="text-xs font-normal opacity-60">
                        Pcs
                      </small>
                    </span>
                  </div>
                  <div className="flex justify-between items-end">
                    <span className="text-slate-300">Estimated Weight</span>
                    <span className="text-2xl font-bold text-orange-400">
                      {viewData?.total_Weight}{" "}
                      <small className="text-xs font-normal opacity-60 text-white">
                        Kg
                      </small>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Model>
      </div>
    </>
  );
};

export default OrderPlanView;
